import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Subject, Observable, throwError as observableThrowError, lastValueFrom } from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from './api-helper.service';
import { APIResponse, GenericAPIArrayResponse, GenericAPIResponse, LoginApiResponse, MfaSummary } from '../models/api-response';
import { AccountRequest, ClipView, EmailInfo, SavedCard, User, UserNotificationSettings, UserProfile, UserRole, UserRoleValue } from '../models/user';
import { TutorialType, UserSettings } from '../models/usersettings';
import { Login, TeamRequest } from '../models/team';
import { ClipPlaylist } from 'src/app/models/clip_playlist';
import { StripePayment, StripePaymentExtraInfo } from '../models/payments';
import { Tournament } from '../models/showcase';
import { Sport } from '../models/conference';
import { Favorite } from '../models/favorites';
import { continuousSports } from '../constants/sport.constants';
import { AthleteMetrics } from '../models/athlete-metrics';
import { InvitationCampaign } from '../models/invitation_campaigns';

@Injectable()
export class UserService {

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService,
    private router: Router
  ) { }


  private onLogout = new Subject();

  listenForLogout(): Observable<any> {
    return this.onLogout.asObservable();
  }

  emitLogout() {
    this.onLogout.next(null);
  }

  performInitialNavigation(user: User, currentRoute?: string) {
    let sportid: Sport;
    if (!user) return;

    let newRoute: string;
    if (user.currentUserRole?.role === 'shooter') newRoute = '/camop-assignments';
    else if (user.currentUserRole?.role === 'camopmanager') newRoute = '/game-manager';
    // else if (user.currentUserRole?.role === 'statsapi') newRoute = '/analyst-dashboard';
    else if (user.currentUserRole?.role === 'statsapi') newRoute = '/analyst-dashboard/tasks';
    else if (user.currentUserRole?.role === 'statmanager') newRoute = '/stat-dashboard';
    else if (user.currentUserRole?.role === 'leagueadmin') newRoute = '/my-video';
    else if (user.currentUserRole?.role === 'fan') newRoute = '/live/home';
		else if (['recruitingcoach'].includes(user.currentUserRole?.role)) newRoute = '/user-account';
		else if (user.currentUserRole?.role === 'thirdparty') newRoute = '/third-party-dashboard';
    else {
      if (user.team?.conference) sportid = user.team.conference.sportid;
      else if (user.team?.sportid) sportid = user.team.sportid;
      else sportid = 'generic';
      
      const leaguePoolOnly = !_.includes(['admin', 'subadmin', 'junioradmin'], user.currentUserRole?.role) && !!user.team?.organization?.leaguepoolonly;
      const hasHomePage = !leaguePoolOnly && _.includes(['admin', 'subadmin', 'junioradmin', 'boardmember', 'teamadmin', 'coach', 'athlete', 'parent', 'thirdparty'], user.currentUserRole?.role);
      if (hasHomePage) newRoute = '/home';
      else if (!leaguePoolOnly && _.includes(continuousSports, sportid)) newRoute = '/my-video-continuous';
      else newRoute = '/my-video';
    }
    
    if (newRoute === currentRoute) window.location.reload();
    else this.router.navigate([newRoute]);
  }

  getUserRoleDisplay(userRole: UserRole) {
    if (!userRole || !userRole.role) return '';
    return this.prettifyRole(userRole.role);
  }
  
  prettifyRole(role: UserRoleValue): string {
    switch(role) {
      case 'subadmin':
        return 'Sub Admin';
      case 'junioradmin':
        return 'Junior Admin';
      case 'teamadmin':
        return 'Team Admin';
      case 'shooter':
        return 'Camera Operator';
      case 'camopmanager':
        return 'CamOp Manager';
      case 'statsapi':
        return 'Statistician';
      case 'statmanager':
        return 'Stat Manager';
      case 'single':
        return 'Single-User Account';
      case 'leagueadmin':
        return 'League Admin';
			case 'thirdparty':
				return 'Third Party';
      default:
        return _.capitalize(role || '');
    }
  }
  
  //api calls
  authenticateUser(email: string, password: string): Promise<LoginApiResponse> {
    const url = `${this.apiHelper.apiUrl()}/users/authenticate`;
    const headers = this.apiHelper.apiHeader();
    const body = {email, password, clientPlatform: 'web', mfaCapable: true};

    return lastValueFrom(this.http.post<LoginApiResponse>(url, body, { headers, withCredentials: true }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendMfaCode(transactionId: string, methodId: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/challenge/send`, {transactionId, methodId}, {headers: this.apiHelper.apiHeader(), withCredentials: true}));
  }

  getMfaPasskeyOptions(transactionId: string, methodId?: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/challenge/passkey-options`, {transactionId, methodId}, {headers: this.apiHelper.apiHeader(), withCredentials: true}));
  }

  verifyMfaChallenge(transactionId: string, methodId: string, code?: string, credential?: any, rememberDevice = true, deviceName = 'Browser'): Promise<LoginApiResponse & {managementToken?: string}> {
    const body = {transactionId, methodId, code, credential, rememberDevice, deviceName, clientPlatform: 'web'};
    return lastValueFrom(this.http.post<LoginApiResponse & {managementToken?: string}>(`${this.apiHelper.apiUrl()}/users/mfa/challenge/verify`, body, {headers: this.apiHelper.apiHeader(), withCredentials: true}));
  }

  getMfaSummary(): Promise<GenericAPIResponse<MfaSummary>> {
    return lastValueFrom(this.http.get<GenericAPIResponse<MfaSummary>>(`${this.apiHelper.apiUrl()}/users/mfa`, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  updateMfaEnrollmentPrompt(action: 'remind_later' | 'dismiss'): Promise<any> {
    return lastValueFrom(this.http.put<any>(`${this.apiHelper.apiUrl()}/users/mfa/enrollment-prompt`, {action}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  startMfaManagement(password: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/manage/start`, {password}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  startMfaEnrollment(type: string, managementToken: string, label?: string, destination?: string, updateUserContact = false): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/enroll/${type}/start`, {managementToken, label, destination, updateUserContact}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  finishMfaEnrollment(type: string, managementToken: string, enrollmentToken: string, code?: string, credential?: any, label?: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/enroll/${type}/finish`, {managementToken, enrollmentToken, code, credential, label}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  updateMfa(managementToken: string, enabled: boolean, defaultMethodId?: string): Promise<any> {
    return lastValueFrom(this.http.put<any>(`${this.apiHelper.apiUrl()}/users/mfa`, {managementToken, enabled, defaultMethodId}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  removeMfaMethod(methodId: string, managementToken: string): Promise<any> {
    return lastValueFrom(this.http.delete<any>(`${this.apiHelper.apiUrl()}/users/mfa/methods/${methodId}`, {headers: this.apiHelper.apiHeaderWithToken().set('x-mfa-management-token', managementToken), withCredentials: true}));
  }

  disableMfa(managementToken: string): Promise<any> {
    return lastValueFrom(this.http.delete<any>(`${this.apiHelper.apiUrl()}/users/mfa`, {headers: this.apiHelper.apiHeaderWithToken().set('x-mfa-management-token', managementToken), withCredentials: true}));
  }

  regenerateMfaRecoveryCodes(managementToken: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.apiHelper.apiUrl()}/users/mfa/recovery-codes/regenerate`, {managementToken}, {headers: this.apiHelper.apiHeaderWithToken(), withCredentials: true}));
  }

  revokeMfaTrustedDevice(deviceId: string, managementToken: string): Promise<any> {
    return lastValueFrom(this.http.delete<any>(`${this.apiHelper.apiUrl()}/users/mfa/trusted-devices/${deviceId}`, {headers: this.apiHelper.apiHeaderWithToken().set('x-mfa-management-token', managementToken), withCredentials: true}));
  }

  adminResetMfa(userid: string, password: string, reason: string, managementToken?: string): Promise<GenericAPIResponse<User>> {
    let headers = this.apiHelper.apiHeaderWithToken();
    if (managementToken) headers = headers.set('x-mfa-management-token', managementToken);
    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(
      `${this.apiHelper.apiUrl()}/users/${userid}/mfa/admin-reset`,
      {password, reason},
      {headers, withCredentials: true}
    ));
  }

  loginAsUser(userid: string): Promise<LoginApiResponse> {
    const url = `${this.apiHelper.apiUrl()}/users/admin-login/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<LoginApiResponse>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createRecruitmentHandoffLink(): Promise<APIResponse> {
    const url = `${this.apiHelper.apiUrl()}/recruitment-app/handoff-token`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<APIResponse>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.error?.message || err.message);
      }),));
  }

  checkRecruitmentHandoffEligibility(): Promise<APIResponse> {
    const url = `${this.apiHelper.apiUrl()}/recruitment-app/handoff-eligibility`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<APIResponse>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.error?.message || err.message);
      }),));
  }

  switchUserRole(userRole: UserRole): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/switch-role`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {userRole};

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUser(): Promise<LoginApiResponse> {
    const url = `${this.apiHelper.apiUrl()}/user`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<LoginApiResponse>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        //console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getBoardmembersByOrgid(organizationid: string): Promise<GenericAPIArrayResponse<User>> {
    const body = {
      organizationid
    }
    const url = `${this.apiHelper.apiUrl()}/users/boardmembers-by-orgid`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resendUserInvites(userids: string[], teamid: string): Promise<GenericAPIResponse<undefined>> {
    const body = {userids, teamid};
    const url = `${this.apiHelper.apiUrl()}/users/resend-invites`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resendAllUserInvites(teamid: string): Promise<GenericAPIResponse<undefined>> {
    const body = {teamid};
    const url = `${this.apiHelper.apiUrl()}/users/resend-invites`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.put<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setPassword(token: string, password: string): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/set-password`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {token, password};

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendPasswordResetEmail(email: string): Promise<GenericAPIResponse<any>> {
    const url = `${this.apiHelper.apiUrl()}/users/password-reset`;
    const headers = this.apiHelper.apiHeader()
    const body = {email};

    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getPasswordResetLink(email: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.apiHelper.apiUrl()}/users/password-reset-link`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {email};

    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  validatePasswordReset(token: string): Promise<GenericAPIResponse<any>> {
    const url = `${this.apiHelper.apiUrl()}/users/password-reset`;
    const headers = this.apiHelper.apiHeader();
    const body = {token};

    return lastValueFrom(this.http.put<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createUsers(users: {username: string, firstname: string, lastname: string, phonenumber?: string, twitter?: string, role: UserRoleValue, teamid?: string, seasons?: number[], title?: string, leaguepoolid?: string, jerseyNumber?: number, 
      subscriptionExpires?: Date, metrics?: AthleteMetrics, singleUser?: boolean, thirdpartyid?: string, teamname?: string, jerseyNumberArray?: {seasonid: string, jerseyNumbers?: number[]}[] }[], skipEmails: boolean = false): Promise<GenericAPIArrayResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/create`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {users, skipEmails};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  moveUserToAnotherTeam(userid: string, oldTeamid: string, newTeamid: string, role: UserRoleValue): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/move`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {userid, oldTeamid, newTeamid, role};

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  searchUsers(searchText: string): Promise<GenericAPIArrayResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/search`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {searchText};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  addDemoUser(username: string, firstname: string, lastname: string, sportid: string): Promise<GenericAPIResponse<User>> {
    const body = {
      username,
      firstname,
      lastname,
      sportid
    };
    const url = `${this.apiHelper.apiUrl()}/users/add-demo-account`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateUser(userid: string, update: any): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/user`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {userid, update};

    return lastValueFrom(this.http.put<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateUserPhoneticSpelling(userid: string, phoneticSpelling: string): Promise<GenericAPIResponse<User>> {
    return this.updateUser(userid, {phoneticSpelling});
  }

  generateUserPhoneticSpelling(userid: string, force: boolean = false): Promise<GenericAPIResponse<{userid: string, phoneticSpelling: string}>> {
    const url = `${this.apiHelper.apiUrl()}/users/${userid}/phonetic-spelling/generate`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {force};

    return lastValueFrom(this.http.post<GenericAPIResponse<{userid: string, phoneticSpelling: string}>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  generateUserPhoneticSpellingFromAudio(userid: string, audioBase64: string, mimeType: string, force: boolean = true): Promise<GenericAPIResponse<{userid: string, phoneticSpelling: string}>> {
    const url = `${this.apiHelper.apiUrl()}/users/${userid}/phonetic-spelling/generate-from-audio`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {audioBase64, mimeType, force};

    return lastValueFrom(this.http.post<GenericAPIResponse<{userid: string, phoneticSpelling: string}>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateUserAndRole(userid: string, data: {teamid?: string, oldRole: UserRoleValue, username: string, firstname: string, lastname: string, phonenumber: string, newRole: UserRoleValue, seasons?: number[], 
		title?: string, leaguepoolid?: string, twitter?: string, jerseyNumber?: number, subscriptionExpires?: Date, singleUser?: boolean, thirdpartyid?: string, sportid?: Sport, positions?: string[], assignmentOrder?: {[position: string]: number},
    jerseyNumberArray?: {seasonid: string, jerseyNumbers?: number[]}[] }): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/user`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {
      userid,
      ...data
    };

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateUsers(updateData: {userid: string, update: any}[]): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {updateData};

    return lastValueFrom(this.http.put<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteUser(userid: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/user/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }
  
  getUsersByIds(userids: string[]): Promise<GenericAPIArrayResponse<User>> {
    const body = {
      userids
    };
    const url = `${this.apiHelper.apiUrl()}/users/get-users-by-ids`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getListUsersName(userids: string[]): Promise<GenericAPIResponse<undefined>> {
    const body = {
      userids
    };
    const url = `${this.apiHelper.apiUrl()}/users/get-list-users-name`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  postUserSettings(userSettings: UserSettings): Promise<GenericAPIResponse<UserSettings>> {
    const body = {sports: userSettings.sports};

    const url = `${this.apiHelper.apiUrl()}/users/settings`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<UserSettings>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  onTutorialViewed(tutorial: TutorialType): Promise<GenericAPIResponse<UserSettings>> {
    const body = {tutorial};

    const url = `${this.apiHelper.apiUrl()}/users/tutorials`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<UserSettings>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserSettings(): Promise<GenericAPIResponse<UserSettings>> {
    const url = `${this.apiHelper.apiUrl()}/users/settings`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIResponse<UserSettings>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  queryUsers(query: any): Promise<GenericAPIArrayResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users`;
    const headers = this.apiHelper.apiHeader();

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<User>>(url, query, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  switchTeam(teamid: string): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/switch-team`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {teamid};

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  addNewFanUser(email: string, firstname: string, lastname: string): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/add-new-fan`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {
      email,
      firstname,
      lastname
    }

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserByUsername(email: string): Promise<GenericAPIResponse<User>> {
    const body = {
      email
    }
    const url = `${this.apiHelper.apiUrl()}/users/get-user-by-username`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUsersByTeamId(teamid: string): Promise<GenericAPIArrayResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/team/${teamid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<User>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  saveLoginInfo(ip: string, region: string, city: string): Promise<GenericAPIResponse<Login>> {
    const url = `${this.apiHelper.apiUrl()}/users/login-info`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {
      ip,
      region,
      city
    }

    return lastValueFrom(this.http.post<GenericAPIResponse<Login>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getLoginInfo(userids: string[]): Promise<GenericAPIArrayResponse<Login>> {
    const url = `${this.apiHelper.apiUrl()}/users/login-info`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {userids};

    return lastValueFrom(this.http.put<GenericAPIArrayResponse<Login>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserById(userid: string): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/get-user-by-id`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {
      userid
    }

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  mediaEncoder(filename: string, gameid: string, clipnumber: number, videoType: string, removeAudio: boolean = false): Promise<GenericAPIResponse<any>> {
    const body = {
      gameid,
      clipnumber,
      filename,
      videoType,
      removeAudio
    }

    const url = `${this.apiHelper.apiUrl()}/users/media-encode`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  imageEncoder(filename: string, storagename: string, invalidate: boolean = false): Promise<GenericAPIResponse<any>> {
    const body = {
      filename,
      storagename,
      invalidate
    };
    const url = `${this.apiHelper.apiUrl()}/users/image-encode`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  requestGameEmail(gamename: string, gamedate: Date, notes: string, email: string): Promise<GenericAPIResponse<any>> {
    const body = {
      gamename,
      gamedate,
      notes,
      email
    }
    const url = `${this.apiHelper.apiUrl()}/users/request-game`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }
  
  createAccountRequest(email: string, firstname: string, lastname: string, phonenumber: string, userrole: UserRoleValue, orgtype: string, sportid: Sport, 
        gender: string, teamname: string, division: string, countryid: string, stateid: string, extrainfo: string, invitecode?: string): Promise<GenericAPIResponse<AccountRequest>> {
    const body = {
      email,
      firstname,
      lastname,
      phonenumber,
      invitecode,
      userrole,
      orgtype,
      sportid,
      gender,
      teamname,
      division,
      countryid,
      stateid,
      extrainfo
    }
    const url = `${this.apiHelper.apiUrl()}/users/account-request`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<AccountRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAccountRequests(start: number = 0, pageSize: number = 50): Promise<GenericAPIArrayResponse<AccountRequest>> {
    const url = `${this.apiHelper.apiUrl()}/users/get-account-requests`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {start, pageSize};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<AccountRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getNumOfAccountRequests(): Promise<GenericAPIResponse<number>> {
    const url = `${this.apiHelper.apiUrl()}/users/get-account-requests`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<number>>(url, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateAccountRequest(_id: string, update: any): Promise<GenericAPIResponse<AccountRequest>> {
    const url = `${this.apiHelper.apiUrl()}/users/account-request`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {
      _id,
      update
    };

    return lastValueFrom(this.http.put<GenericAPIResponse<AccountRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendAccountDenyEmail(email: string, message: string): Promise<GenericAPIResponse<any>> {
    const body = {
      email,
      message
    };
    const url = `${this.apiHelper.apiUrl()}/users/deny-email`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createTeamRequest(email: string, firstname: string, lastname: string, teamid: string, userrole: UserRoleValue = 'athlete', phonenumber?: string, jerseyNumber?: number): Promise<GenericAPIResponse<TeamRequest>> {
    const body = {
      email,
      firstname,
      lastname,
      teamid,
      userrole,
			phonenumber,
			jerseyNumber
    };
    const url = `${this.apiHelper.apiUrl()}/users/team-request`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIResponse<TeamRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamRequests(teamid: string): Promise<GenericAPIArrayResponse<TeamRequest>> {
    let body = {teamid};

    const url = `${this.apiHelper.apiUrl()}/users/get-team-requests`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<TeamRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateTeamRequest(_id: string, update: any): Promise<GenericAPIResponse<TeamRequest>> {
    const url = `${this.apiHelper.apiUrl()}/users/team-request`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {
      _id,
      update
    };

    return lastValueFrom(this.http.put<GenericAPIResponse<TeamRequest>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  approveTeamRequest(_id: string): Promise<GenericAPIResponse<User>> {
    const url = `${this.apiHelper.apiUrl()}/users/approve-team-request`;
    const headers = this.apiHelper.apiHeaderWithToken()
    const body = {
      _id
    };

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError((err: any) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserIdByOldId(oldid: number): Promise<GenericAPIResponse<string>> {
    const url = `${this.apiHelper.apiUrl()}/users/get-userid-by-oldid/${oldid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<string>>(url, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  broadcastSocketio(eventName: string, data: any): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/users/broadcast-socketio`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {eventName, data};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map((res) => res),
      catchError((err) => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getClipPlaylists(): Promise<GenericAPIArrayResponse<ClipPlaylist>> {
    const url = `${this.apiHelper.apiUrl()}/users/get-clip-playlist`;
    const headers = this.apiHelper.apiHeaderWithToken()

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<ClipPlaylist>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        return observableThrowError(err.message);
      })));
  }

  getUserNotificationSettings(userid: string): Promise<GenericAPIResponse<UserNotificationSettings>> {
    const url = `${this.apiHelper.apiUrl()}/users/notifications/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<UserNotificationSettings>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  submitUserNotificationsSettings(userid: string, update: Partial<UserNotificationSettings>): Promise<GenericAPIResponse<UserNotificationSettings>> {
    const body = {update};
    const url = `${this.apiHelper.apiUrl()}/users/notifications/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<UserNotificationSettings>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUserProfile(userid: string): Promise<GenericAPIResponse<UserProfile>> {
    const url = `${this.apiHelper.apiUrl()}/users/profile/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<UserProfile>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  submitUserProfile(userid: string, update: Partial<UserProfile>): Promise<GenericAPIResponse<UserProfile>> {
    const body = {update};
    const url = `${this.apiHelper.apiUrl()}/users/profile/${userid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<UserProfile>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createInvitationCampaign(data: Partial<InvitationCampaign>): Promise<GenericAPIResponse<InvitationCampaign>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<InvitationCampaign>>(url, data, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAllInvitationCampaigns(): Promise<GenericAPIArrayResponse<InvitationCampaign>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<InvitationCampaign>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateInvitationCampaign(_id: string, update: Partial<InvitationCampaign>): Promise<GenericAPIResponse<InvitationCampaign>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {update};

    return lastValueFrom(this.http.put<GenericAPIResponse<InvitationCampaign>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteInvitationCampaign(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getActiveInvitationCampaign(coachid: string): Promise<GenericAPIResponse<InvitationCampaign>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign/${coachid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<InvitationCampaign>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  completeInvitationCampaign(_id: string): Promise<GenericAPIResponse<InvitationCampaign>> {
    const url = `${this.apiHelper.apiUrl()}/users/invitation-campaign/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<InvitationCampaign>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getEmailInfo(email: string): Promise<GenericAPIResponse<EmailInfo>> {
		const body = {email};
    const url = `${this.apiHelper.apiUrl()}/users/email-info`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<EmailInfo>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getEmailInfos(emails: string[]): Promise<GenericAPIArrayResponse<EmailInfo>> {
		const body = {emails};
    const url = `${this.apiHelper.apiUrl()}/users/email-infos`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<EmailInfo>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  clearEmailInfo(email: string): Promise<GenericAPIResponse<EmailInfo>> {
		const body = {email};
    const url = `${this.apiHelper.apiUrl()}/users/email-info`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<EmailInfo>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  clearEmailInfoForTeam(teamid: string): Promise<GenericAPIResponse<number>> {
		const body = {teamid};
    const url = `${this.apiHelper.apiUrl()}/users/team-email-info`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<number>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  mergeUsers(masterUserid: string, useridsToMerge: string[]): Promise<GenericAPIResponse<User>> {
		const body = {masterUserid, useridsToMerge};
    const url = `${this.apiHelper.apiUrl()}/users/merge-users`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<User>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }
}
